import { useState, useEffect, useCallback } from "react";
import { getBanners } from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Carousel hook for auto-sliding banners
function useCarousel(items, intervalMs = 5000) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [items.length, intervalMs]);

  const goTo = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  return { currentIndex, goTo, next, prev };
}

export function SidebarBanner() {
  const [banners, setBanners] = useState([]);
  const { currentIndex, next, prev } = useCarousel(banners, 6000);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const res = await getBanners({ position: "sidebar", active_only: true });
      setBanners(res.data);
    } catch (error) {
      console.error("Error loading banners:", error);
    }
  };

  if (banners.length === 0) return null;

  const banner = banners[currentIndex];

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:block" data-testid="sidebar-banner">
      <div className="relative">
        <a
          href={banner.link_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:opacity-90 transition-opacity shadow-lg rounded-lg overflow-hidden"
        >
          {banner.image_base64 ? (
            <img
              src={`data:image/jpeg;base64,${banner.image_base64}`}
              alt={banner.alt_text || banner.name}
              className="w-[140px] lg:w-[160px] h-auto"
            />
          ) : banner.image_url ? (
            <img
              src={banner.image_url}
              alt={banner.alt_text || banner.name}
              className="w-[140px] lg:w-[160px] h-auto"
            />
          ) : null}
        </a>

        {/* Carousel controls (only if multiple banners) */}
        {banners.length > 1 && (
          <>
            <div className="absolute -bottom-6 left-0 right-0 flex justify-center gap-1.5">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { }}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentIndex ? "bg-accent" : "bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={prev}
              className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-primary" />
            </button>
            <button
              onClick={next}
              className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-primary" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function BottomBanner() {
  const [banners, setBanners] = useState([]);
  const { currentIndex, goTo, next, prev } = useCarousel(banners, 5000);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const res = await getBanners({ position: "bottom", active_only: true });
      setBanners(res.data);
    } catch (error) {
      console.error("Error loading banners:", error);
    }
  };

  if (banners.length === 0) return null;

  // If only a few banners, show all. If many, use carousel
  const showCarousel = banners.length > 3;

  return (
    <section className="bg-secondary border-t border-border py-8" data-testid="bottom-banner">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {showCarousel ? (
          <div className="relative">
            <div className="flex justify-center">
              <a
                href={banners[currentIndex].link_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-90 transition-opacity"
              >
                {banners[currentIndex].image_base64 ? (
                  <img
                    src={`data:image/jpeg;base64,${banners[currentIndex].image_base64}`}
                    alt={banners[currentIndex].alt_text || banners[currentIndex].name}
                    className="h-[90px] w-auto rounded"
                  />
                ) : banners[currentIndex].image_url ? (
                  <img
                    src={banners[currentIndex].image_url}
                    alt={banners[currentIndex].alt_text || banners[currentIndex].name}
                    className="h-[90px] w-auto rounded"
                  />
                ) : null}
              </a>
            </div>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentIndex ? "bg-accent" : "bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>
            {/* Arrows */}
            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full shadow flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-primary" />
            </button>
            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full shadow flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-6">
            {banners.map((banner) => (
              <a
                key={banner.id}
                href={banner.link_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-90 transition-opacity"
              >
                {banner.image_base64 ? (
                  <img
                    src={`data:image/jpeg;base64,${banner.image_base64}`}
                    alt={banner.alt_text || banner.name}
                    className="h-[90px] w-auto rounded"
                  />
                ) : banner.image_url ? (
                  <img
                    src={banner.image_url}
                    alt={banner.alt_text || banner.name}
                    className="h-[90px] w-auto rounded"
                  />
                ) : null}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function ArticleBanner() {
  const [banners, setBanners] = useState([]);
  const { currentIndex } = useCarousel(banners, 8000);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const res = await getBanners({ position: "article", active_only: true });
      setBanners(res.data);
    } catch (error) {
      console.error("Error loading banners:", error);
    }
  };

  if (banners.length === 0) return null;

  const banner = banners[currentIndex];

  return (
    <div className="my-8 text-center" data-testid="article-banner">
      <a
        href={banner.link_url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block hover:opacity-90 transition-opacity"
      >
        {banner.image_base64 ? (
          <img
            src={`data:image/jpeg;base64,${banner.image_base64}`}
            alt={banner.alt_text || banner.name}
            className="max-w-full h-auto rounded-lg"
          />
        ) : banner.image_url ? (
          <img
            src={banner.image_url}
            alt={banner.alt_text || banner.name}
            className="max-w-full h-auto rounded-lg"
          />
        ) : null}
      </a>
      <p className="text-xs text-muted-foreground mt-1">Publicité</p>
    </div>
  );
}
